import React, { useMemo, useState } from "react";
import "./PaymentDetails.css";
import Widgets from "../../../widgets";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import strings from "../../../utils/strings";
import Icons from "../../../Assets/Icons";
import spinnerIcon from "../../../Assets/Icons/spinner-solid 1.png";
import IconFemale from "../../../Assets/Icons/venus-mars-solid 1.png";
import call from "../../../Assets/Icons/call.png";
import shields from "../../../Assets/Icons/shield-halved-solid 1.png";

const formatDisplayDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
};

const formatDOB = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d.getDate().toString().padStart(2, "0")} ${months[d.getMonth()]}, ${d.getFullYear()}`;
};

const formatTransactionDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}, ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`;
};

const sessionTotal = (sess) =>
  (sess.additionalConsumables || []).reduce(
    (sum, c) => sum + parseFloat(c.consumable?.price || 0) * (c.quantity || 0),
    0
  );

const PaymentDetails = () => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();
  const { request, session: selectedSession } = location.state || {};

  const [expandedSessions, setExpandedSessions] = useState({});
  const [showAbscondModal, setShowAbscondModal] = useState(false);
  const [distanceKm, setDistanceKm] = useState("");
  const [selectedCondition, setSelectedCondition] = useState(null);

  const patient = request?.patient || {};
  const allSessions = request?.sessions || (selectedSession ? [selectedSession] : []);

  const sessionRows = useMemo(() =>
    allSessions.map((sess, idx) => {
      const consumables = sess.additionalConsumables || [];
      const total = sessionTotal(sess);
      const items = consumables.map((c, i) => ({
        slNo: String(i + 1).padStart(2, "0"),
        itemNo: c.consumable?.code || "-",
        itemName: c.consumable?.itemName?.en || "-",
        quantity: c.quantity || 0,
        costPerItem: parseFloat(c.consumable?.price || 0),
        totalFee: parseFloat(c.consumable?.price || 0) * (c.quantity || 0),
      }));
      const canExpand = items.length > 0;
      return {
        _id: sess._id,
        date: formatDisplayDate(sess.date),
        rawDate: sess.date,
        status: sess.additionalConsumablePaymentStatus,
        transactionId: sess.additionalConsumableTransactionId,
        items,
        total,
        canExpand,
        expanded: Boolean(expandedSessions[idx]),
      };
    }),
    [allSessions, expandedSessions]
  );

  const grandTotal = sessionRows.reduce((s, r) => s + r.total, 0);
  const grandPaid = sessionRows.reduce((s, r) => s + (r.status === "COMPLETED" ? r.total : 0), 0);
  const grandRemaining = grandTotal - grandPaid;

  const overallStatus = selectedSession?.additionalConsumablePaymentStatus || allSessions[0]?.additionalConsumablePaymentStatus || "OUTSTANDING";

  const toggleSession = (idx) => {
    if (!sessionRows[idx].canExpand) return;
    setExpandedSessions((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const statusLabel = (s) => {
    if (s === "OUTSTANDING") return t("Outstanding") || "Outstanding";
    if (s === "COMPLETED") return t("Paid") || "Paid";
    if (s === "ABSCONDED") return t("Absconded") || "Absconded";
    return s || "-";
  };

  const conditionOptions = [
    { value: "stable", label: t("stable") },
    { value: "critical", label: t("critical") },
    { value: "under-observation", label: t("underObservation") },
  ];

  const renderAddressWithBreak = (address) => {
    if (!address) return "-";
    const idx = address.toLowerCase().indexOf("district");
    if (idx === -1) return address;
    const commaIdx = address.indexOf(",", idx);
    if (commaIdx === -1) return address;
    return (
      <>
        {address.slice(0, commaIdx + 1)}
        <br />
        {address.slice(commaIdx + 1).trimStart()}
      </>
    );
  };

  return (
    <div className="payment-details-container">
      <Widgets.BreadCrumb
        breadcrumbs={["nursingCare", "additionalCostManagement", "paymentDetails"]}
        paths={[
          strings.navigation.nursingCareManageService,
          strings.navigation.additionalCostManagement,
          strings.navigation.paymentDetails,
        ]}
      />

      {/* Header */}
      <div className="pd-header">
        <h1 className="pd-title">{t("additionalBillingPaymentDetails")}</h1>
        <span className={`pd-status-badge ${overallStatus === "COMPLETED" ? "status-paid" : overallStatus === "ABSCONDED" ? "status-absconded" : "status-outstanding"}`}>
          {overallStatus === "OUTSTANDING" && <img src={spinnerIcon} alt="" className="pd-status-icon" />}
          {overallStatus === "COMPLETED" && <img src={Icons.checkSolid} alt="" className="pd-status-icon" />}
          {overallStatus === "ABSCONDED" && <img src={Icons.abIcon} alt="" className="pd-status-icon" />}
          {statusLabel(overallStatus)}
        </span>
      </div>

      {/* Patient Information Card */}
      <div className="pd-card">
        <div className="pd-card-header">
          <h2 className="pd-section-title">{t("patientInformation")}</h2>
          <button className="pd-view-request-btn" onClick={() => nav(-1)}>{t("viewRequestDetails")}</button>
        </div>

        <div className="pd-patient-content">
          <div className="pd-patient-left">
            <img
              src={patient.profilePhoto || "https://i.pravatar.cc/120?img=47"}
              alt="Patient"
              className="pd-patient-avatar"
            />
            <div className="pd-hn-box">
              <div className="pd-hn-label">{t("hospitalNumber")}</div>
              <div className="pd-hn-value">{patient.hn || "-"}</div>
            </div>
          </div>

          <div className="pd-patient-right">
            <h3 className="pd-patient-name">{patient.fullName || "-"}</h3>

            <div className="pd-info-grid">
              <div className="pd-info-item">
                <img src={IconFemale} alt="" className="pd-icon" />
                <span>{patient.gender ? t(patient.gender) : "-"}</span>
              </div>
              <div className="pd-info-item dates">
                <img src={Icons.CalenderIcon} alt="" className="pd-icon" />
                <span>{formatDOB(patient.dateOfBirth)}</span>
              </div>
              <div className="pd-info-item">
                <img src={call} alt="" className="pd-icon" />
                <span>{patient.countryCode ? `+${patient.countryCode} ${patient.phoneNumber}` : patient.phoneNumber || "-"}</span>
              </div>
              <div className="pd-info-item gmails">
                <img src={Icons.SmsIcon} alt="" className="pd-icon" />
                <span>{patient.email || "-"}</span>
              </div>
            </div>

            <div className="pd-info-item pd-insurance insurence">
              <img src={shields} alt="" className="pd-icon" />
              <span>{patient.insurance || "-"}</span>
            </div>

            <div className="pd-condition-wrapper">
              <label className="pd-condition-label">{t("patientCondition")}</label>
              <Widgets.NewSelectDropdown
                options={conditionOptions}
                selectedValue={selectedCondition}
                onChange={setSelectedCondition}
                placeholder={"select"}
                big
              />
            </div>
          </div>
        </div>

        <div className="pd-address-zone-row">
          <div className="pd-address-section">
            <div className="pd-address-label">
              <img src={Icons.Location} alt="" className="pd-icon" />
              <span>{t("patientAddress")}</span>
            </div>
            <div className="address-verified-wrapper">
              <input type="checkbox" aria-label="address-verified" />
              <p className="pd-address-text">
                {renderAddressWithBreak(patient.address)}
              </p>
            </div>
          </div>

          <div className="pd-zone-section">
            <label className="pd-zone-label">
              {t("zone")}<span className="pd-required">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.1"
              className="pd-zone-input"
              value={distanceKm}
              onChange={(e) => setDistanceKm(e.target.value)}
              placeholder={t("noofkm")}
            />
          </div>
        </div>
      </div>

      {/* Sessions Breakdown Card */}
      <div className="pd-card">
        <div className="pd-card-header">
          <h2 className="pd-section-title">{t("sessionsBreakdown")}</h2>
        </div>

        <div className="pd-sessions-content">
          <table className="pd-sessions-table">
            <colgroup>
              <col className="session-date" />
              <col className="items-used" />
              <col className="total-fee" />
            </colgroup>
            <thead>
              <tr>
                <th>{t("sessionDate")}</th>
                <th>{t("itemsUsed")}</th>
                <th>{t("totalFee")}</th>
              </tr>
            </thead>
            <tbody>
              {sessionRows.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: "center", padding: "16px", color: "#888" }}>{t("noData")}</td></tr>
              ) : sessionRows.map((session, index) => (
                <React.Fragment key={session._id || index}>
                  <tr className="pd-session-row">
                    <td>{session.date}</td>
                    <td>
                      {session.canExpand ? (
                        <button
                          type="button"
                          className="pd-collapse-link"
                          onClick={() => toggleSession(index)}
                          aria-expanded={session.expanded}
                        >
                          {session.expanded ? t("clickToCollapse") : t("clickToExpand")}
                        </button>
                      ) : (
                        <span className="pd-collapse-link pd-collapse-link-disabled" aria-disabled="true">
                          {t("availableAfterSessionDate") || "Available after session date"}
                        </span>
                      )}
                    </td>
                    <td className="pd-fee">${session.total.toFixed(2)}</td>
                  </tr>

                  {session.expanded && (
                    <tr className="pd-session-details-row">
                      <td colSpan="3">
                        <table className="pd-session-detail-table">
                          <colgroup>
                            <col className="slno" />
                            <col className="itemno" />
                            <col className="qty" />
                            <col className="cost" />
                            <col className="total" />
                          </colgroup>
                          <thead>
                            <tr>
                              <th>{t("slNo")}</th>
                              <th>{t("itemNo")}</th>
                              <th>{t("quantity")}</th>
                              <th>{t("costPerItem")}</th>
                              <th>{t("totalFee")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {session.items.length > 0 ? (
                              <>
                                {session.items.map((item, itemIndex) => (
                                  <tr key={itemIndex} className="pd-detail-row">
                                    <td>{item.slNo}</td>
                                    <td>{item.itemNo}</td>
                                    <td>{item.quantity}</td>
                                    <td>${item.costPerItem.toFixed(2)}</td>
                                    <td>${item.totalFee.toFixed(2)}</td>
                                  </tr>
                                ))}
                                <tr className="pd-subtotal-row">
                                  <td colSpan="4">{t("total")}</td>
                                  <td>${session.total.toFixed(2)}</td>
                                </tr>
                              </>
                            ) : (
                              <tr className="pd-no-items-row">
                                <td colSpan="5">{t("noItemsUsed")}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Details & Transaction History Card */}
      <div className="pd-card">
        <div className="pd-card-header">
          <h2 className="pd-section-title">{t("paymentDetailsAndTransactionHistory")}</h2>
        </div>

        <div className="pd-payment-content">
          <table className="pd-payment-summary-table">
            <thead>
              <tr>
                <th>{t("totalAmount")}</th>
                <th>{t("amountPaid")}</th>
                <th>{t("remainingBalance")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="pd-amount">${grandTotal.toFixed(2)}</td>
                <td className="pd-amount">${grandPaid.toFixed(2)}</td>
                <td className="pd-amount pd-balance">${grandRemaining.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <button className="pd-mark-absconding-btn" onClick={() => setShowAbscondModal(true)}>
            {t("markAsAbsconding")}
          </button>

          <table className="pd-transaction-table">
            <thead>
              <tr>
                <th>{t("date")}</th>
                <th>{t("amountPaid")}</th>
                <th>{t("paymentMode")}</th>
                <th>{t("paymentStatus")}</th>
                <th>{t("healthProfessionalId")}</th>
                <th>{t("action")}</th>
              </tr>
            </thead>
            <tbody>
              {sessionRows.filter((s) => s.transactionId).length === 0 ? (
                <tr>
                  <td colSpan="6" className="pd-no-data">{t("noData")}</td>
                </tr>
              ) : (
                sessionRows
                  .filter((s) => s.transactionId)
                  .map((s, i) => (
                    <tr key={i}>
                      <td>{formatTransactionDate(s.rawDate)}</td>
                      <td>${(s.status === "COMPLETED" ? s.total : 0).toFixed(2)}</td>
                      <td>-</td>
                      <td>
                        <span className={`status-pill ${s.status === "COMPLETED" ? "status-paid" : s.status === "ABSCONDED" ? "status-absconded" : "status-outstanding"}`}>
                          {statusLabel(s.status)}
                        </span>
                      </td>
                      <td>{s.transactionId}</td>
                      <td>-</td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAbscondModal && (
        <div className="status-modal-overlay">
          <div className="status-modal open">
            <div className="status-modal-header">{t("markAsAbsconding")}</div>
            <div className="status-modal-sub">{t("abscondingConfirmDesc")}</div>
            <div className="status-modal-actions">
              <button className="status-cancel-btn" onClick={() => setShowAbscondModal(false)}>
                {t("cancel")}
              </button>
              <button className="status-confirm-btn" onClick={() => setShowAbscondModal(false)}>
                {t("confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentDetails;
